/** Removes column preferences that refer to columns no longer defined in the table */
export function pruneOrphanedColumns(preferences, validColumnsSet) {
  for (const key of Object.keys(preferences)) {
    if (!validColumnsSet.has(key)) {
      delete preferences[key];
    }
  }
}

/**
 * Computes the final list of visible columns in the correct order.
 * Combines data columns (that are visible) with always-visible columns appended last.
 */
export function getVisibleOrderedColumns(preferences, dataColumns, alwaysVisibleColumns, validColumnsSet, alwaysVisibleSet) {
  const baseVisibleColumns = preferences ? Object.entries(preferences).filter(([key, pref]) => validColumnsSet.has(key) && !alwaysVisibleSet.has(key) && pref.visible).sort(([, a], [, b]) => a.order - b.order).map(([key]) => key) : dataColumns;
  return [...baseVisibleColumns, ...alwaysVisibleColumns];
}

/** Swaps the order of two columns in the preferences map, given the moved field and the target index */
export function swapColumnOrder(preferences, visibleFields, oldIndex, targetIndex, hasCheckboxColumn = true) {
  const updated = {};
  for (const key in preferences) {
    updated[key] = {
      ...preferences[key]
    };
  }
  const offset = hasCheckboxColumn ? 1 : 0;
  const from = oldIndex - offset;
  const to = targetIndex - offset;
  const newOrder = [...visibleFields];
  newOrder.splice(to, 0, newOrder.splice(from, 1)[0]);
  newOrder.forEach((field, i) => {
    updated[field].order = i + 1;
    updated[field].visible = true;
  });
  return updated;
}

/**
 * Updates the column preferences map to reflect the current set of selected (visible) columns.
 *
 * - Sets the `visible` property to `true` for columns present in `selectedKeys`, and `false` otherwise.
 * - Preserves the existing `order` property for all columns.
 * - Ensures the "options" column (if present) is always visible.
 */
export function updatePreferencesWithOrder(columnPreferences, selectedKeys) {
  const updatedPreferences = {};
  for (const [key, pref] of Object.entries(columnPreferences)) {
    const willBeVisible = selectedKeys.has(key);
    updatedPreferences[key] = {
      ...pref,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      visible: willBeVisible,
      order: pref.order
    };
  }
  if (updatedPreferences.options) {
    updatedPreferences.options.visible = true;
  }
  return updatedPreferences;
}

/**
 * Normalizes the order of columns in the preferences map.
 *
 * - Assigns sequential order (1, 2, 3, ...) to all visible columns except those in alwaysVisibleColumns.
 * - Ensures all alwaysVisibleColumns (e.g. "options") that are visible are placed at the end, with the highest order values.
 * - Hidden columns retain their previous order value.
 */
export function normalizeColumnOrders(preferences, alwaysVisibleColumns = []) {
  const alwaysVisibleSet = new Set(alwaysVisibleColumns);
  const updated = {
    ...preferences
  };
  const visibleColumns = [];
  const hiddenColumns = [];
  Object.keys(updated).forEach(k => {
    if (updated[k].visible && !alwaysVisibleSet.has(k)) {
      visibleColumns.push(k);
    } else if (!alwaysVisibleSet.has(k)) {
      hiddenColumns.push(k);
    }
  });
  visibleColumns.sort((a, b) => updated[a].order - updated[b].order);
  hiddenColumns.sort((a, b) => updated[a].order - updated[b].order);
  const all = [...visibleColumns, ...hiddenColumns, ...alwaysVisibleColumns];
  let order = 1;
  for (const k of all) {
    updated[k].order = order++;
    if (alwaysVisibleSet.has(k)) {
      updated[k].visible = true;
    }
  }
  return updated;
}

/**
 * Builds the preference map of a table out of the preferences that are already known.
 *
 * It guarantees that:
 * - preferences of columns that are not part of the table anymore are dropped
 * - every data column has an entry, new ones are appended after the existing columns and are
 *   visible unless a list of default visible columns says otherwise
 * - the non-configurable columns exist, stay visible and are placed after the data columns
 *
 * Columns can appear after the first render, e.g. the SCENE column of the workspace members table
 * is only known once the workspace subscriptions are fetched, so this runs both on the preferences
 * fetched from the settings API and on the current preferences whenever the columns change.
 */
export function mergeColumnPreferences(knownPreferences, dataColumns, alwaysVisibleColumns, validColumnsSet, defaultVisibleColumns) {
  const mergedPreferences = {
    ...knownPreferences
  };
  pruneOrphanedColumns(mergedPreferences, validColumnsSet);

  // New columns are appended after the known ones, so their order does not clash with them
  let nextOrder = dataColumns.reduce((maxOrder, column) => mergedPreferences[column] ? Math.max(maxOrder, mergedPreferences[column].order) : maxOrder, 0);
  for (const column of dataColumns) {
    if (!mergedPreferences[column]) {
      nextOrder += 1;
      mergedPreferences[column] = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        visible: defaultVisibleColumns ? defaultVisibleColumns.includes(column) : true,
        order: nextOrder,
        sortBy: null
      };
    }
  }
  for (const [idx, column] of alwaysVisibleColumns.entries()) {
    mergedPreferences[column] = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      visible: true,
      order: dataColumns.length + idx + 1,
      sortBy: null
    };
  }
  return mergedPreferences;
}